import * as React from 'react';

import { useQuery } from '@apollo/client';

import { getCmsLocaleString } from 'js/lib/language';

import type {
  AllExtraMetadataQuery,
  AllExtraMetadataQueryVariables,
} from 'bundles/search-common/__generated__/contentfulTypes';
import isPremiumProduct from 'bundles/search-common/components/search-cards/isPremiumProduct';
import type { LEARNING_PRODUCTS } from 'bundles/search-common/constants';
import type { SearchHit, SearchUpsellHit } from 'bundles/search-common/providers/searchTypes';
import getAllExtraMetadataQuery from 'bundles/search-common/queries/getAllExtraMetadataQuery';
import { getHitSlug } from 'bundles/search-common/utils/SearchUtils';
import { isSearchProductHit } from 'bundles/search-common/utils/utils';

type PremiumProductMetadata = {
  slug?: string;
  atAGlance?: { timeFrame?: string };
};

// Only call contentful if the slug is defined and the data is not already cached
export const filterPremiumHits = ({
  hits,
  existingData,
}: {
  hits?: Array<SearchHit | SearchUpsellHit>;
  existingData: Record<string, unknown>;
}) => {
  const premiumProducts =
    hits?.filter(
      (hit) =>
        hit?.__typename === 'Search_ProductHit' &&
        hit?.productType &&
        isPremiumProduct(hit?.productType as (typeof LEARNING_PRODUCTS)[keyof typeof LEARNING_PRODUCTS])
    ) || [];

  const hitsWithSlug = premiumProducts
    .map((hit) => ({ slug: isSearchProductHit(hit) ? getHitSlug(hit.url || undefined) : undefined }))
    .filter((hit) => !!hit.slug && !(hit.slug in existingData));

  return { hitsWithSlug };
};

/**
 * @deprecated
 * TODO: REMOVE THIS GRAPHGL CALL!!!
 * THIS BREAKS THE RULES WE DEFINED FOR UNIFIED PLATFORM
 * This graph call should be removed as part of (https://coursera.atlassian.net/browse/GR-28924
 * and https://coursera.atlassian.net/browse/SEARCH-1254)
 *
 * Custom hook to fetch premium product metadata from Contentful.
 * This hook handles fetching duration information for premium products (degrees and mastertracks)
 * and returns the premium product duration data.
 */
export const usePremiumProductMetadata = (hits?: Array<SearchHit | SearchUpsellHit>) => {
  // Store premium product durations in local state instead of global variable
  const [premiumProductDurations, setPremiumProductDurations] = React.useState<Record<string, unknown>>({});

  const { hitsWithSlug } = filterPremiumHits({ hits, existingData: premiumProductDurations });

  const { data, loading, error } = useQuery<AllExtraMetadataQuery, AllExtraMetadataQueryVariables>(
    getAllExtraMetadataQuery,
    {
      variables: {
        degreeSlugs: hitsWithSlug,
        mastertrackSlugs: hitsWithSlug,
        locale: getCmsLocaleString(),
      },
      context: { clientName: 'contentfulGql' },
      fetchPolicy: 'cache-first',
      ssr: false,
      skip: hitsWithSlug.length === 0, // Skip query if no premium hits with slugs
    }
  );

  // Process the data and update the local state when data changes
  React.useEffect(() => {
    if (data) {
      const degreesData = data?.universityProgramDegreeDescriptionPageCollection?.items;
      const mastertrackData = data?.premiumCertificatePageContentCollection?.items;

      const newDurations: Record<string, unknown> = {};

      if (degreesData) {
        degreesData.forEach((degree) => {
          const slug = (degree as PremiumProductMetadata)?.slug;
          const duration = (degree as PremiumProductMetadata)?.atAGlance?.timeFrame;
          if (slug) {
            newDurations[slug] = duration;
          }
        });
      }

      if (mastertrackData) {
        mastertrackData.forEach((mastertrack) => {
          const slug = (mastertrack as PremiumProductMetadata)?.slug;
          const duration = (mastertrack as PremiumProductMetadata)?.atAGlance?.timeFrame;
          if (slug) {
            newDurations[slug] = duration;
          }
        });
      }

      if (Object.keys(newDurations).length > 0) {
        setPremiumProductDurations((prev) => ({ ...prev, ...newDurations }));
      }
    }
  }, [data]);

  return {
    loading,
    error,
    hasData: !!data,
    premiumProductDurations, // Return the actual duration data
  };
};
